// assets/AllCourses/Exercises/MissingAlphabetsExercise.jsx

import React, { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import useExerciseEngine from "@/hooks/useExerciseEngine";

export default function MissingAlphabetsExercise({
  exercise = {},
  userAnswer = {},
  onUpdate,
  isPreview = false,
  showResults = false,
  onComplete = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.missingAlphabetsQuestions)
    ? exercise.missingAlphabetsQuestions
    : [];

  /* ================= SAFE EXTERNAL ANSWERS ================= */
  const safeExternalAnswers =
    typeof userAnswer?.answer === "object" ? userAnswer.answer : {};

  /* ================= ENGINE ================= */
  const { answers, setAnswer, getInputState } = useExerciseEngine({
    questions: questions.map((q) => ({
      type: "fill",
      answers: q.answer,
      requireCapitalization: q.requireCapitalization,
      skill: "spelling",
    })),
    showResult: showResults,
    externalAnswers: safeExternalAnswers,
  });

  /* ================= SYNC WITH PARENT ================= */
  const prevRef = useRef({});

  useEffect(() => {
    if (isPreview) return;

    const isSame =
      JSON.stringify(prevRef.current) === JSON.stringify(answers);

    if (isSame) return;

    prevRef.current = answers;

    onComplete({
      answer: answers,
    });
  }, [answers]);

  /* ================= EDIT MODE ================= */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("missingAlphabetsQuestions", next);
  };

  const updateWord = (idx, value) => {
    const next = [...questions];

    const blanks = (value.match(/_+/g) || []).length;
    const prev = next[idx]?.answer || [];

    next[idx] = {
      ...next[idx],
      word: value,
      answer: Array.from({ length: blanks }, (_, i) => prev[i] || ""),
    };

    updateQuestions(next);
  };

  /* ================= EDIT UI ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, idx) => {
          const blanks = (q.word.match(/_+/g) || []).length;

          return (
            <Card key={idx} className="p-6">
              <div className="space-y-6">
                <StringField
                  label={`Word ${idx + 1}`}
                  value={q.word || ""}
                  onChange={(v) => updateWord(idx, v)}
                  placeholder="A__le"
                />

                {Array.from({ length: blanks }).map((_, i) => (
                  <StringField
                    key={i}
                    label={`Letter ${i + 1}`}
                    value={q.answer[i] || ""}
                    onChange={(v) => {
                      const next = [...questions];
                      next[idx].answer[i] = v;
                      updateQuestions(next);
                    }}
                    maxLength={1}
                  />
                ))}

                <CapitalizationRule
                  checked={!!q.requireCapitalization}
                  onChange={(c) => {
                    const next = [...questions];
                    next[idx].requireCapitalization = c;
                    updateQuestions(next);
                  }}
                />

                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() =>
                    updateQuestions(questions.filter((_, i) => i !== idx))
                  }
                >
                  Remove
                </Button>
              </div>
            </Card>
          );
        })}

        <Button
          variant="outline"
          onClick={() =>
            updateQuestions([
              ...questions,
              { word: "", answer: [], requireCapitalization: false },
            ])
          }
        >
          Add Word
        </Button>
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>No questions available.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT UI ================= */
  return (
    <Card className="max-w-3xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle>Missing Letters</CardTitle>
        <CardDescription>
          Fill the missing letters
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, qIdx) => {
          const parts = q.word.split(/_+/g);
          let blankIdx = 0;

          return (
            <div
              key={qIdx}
              className="rounded-xl border bg-muted/40 p-6 flex flex-wrap gap-3 justify-center"
            >
              {parts.map((part, i) => {
                if (i === parts.length - 1) {
                  return <span key={i}>{part}</span>;
                }

                const bIdx = blankIdx++;
                const key = `${exercise._id}-${qIdx}-${bIdx}`;

                const value = answers[key] || "";
                const correct = q.answer[bIdx];

                const state = getInputState(value, correct);

                return (
                  <React.Fragment key={i}>
                    <span>{part}</span>

                    <Input
                      maxLength={1}
                      value={value}
                      disabled={showResults}
                      onChange={(e) => {
                        setAnswer(key, e.target.value);
                      }}
                      className={`w-14 h-14 text-center text-2xl font-bold ${state.className}`}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}